import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, ArrowRight, ShieldCheck, CheckCircle2, ArrowLeft } from "lucide-react";

/**
 * WachtwoordVergeten — vraag reset-link aan.
 * URL: /wachtwoord-vergeten
 *
 * Backend geeft altijd 200 (voorkomt email-enumeratie), dus we tonen ook
 * altijd een "check je mail"-scherm.
 */
export default function WachtwoordVergeten() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    document.title = "BPTplus · Wachtwoord vergeten";
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await api.post("/auth/wachtwoord-vergeten", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Er ging iets mis");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-12" data-testid="wachtwoord-vergeten-page">
      {/* Links brand-panel */}
      <aside
        className="hidden lg:flex lg:col-span-5 xl:col-span-6 relative overflow-hidden text-white flex-col justify-between p-12 xl:p-16"
        style={{ background: "linear-gradient(160deg, #0b1120 0%, #111827 60%, #0f172a 100%)" }}
      >
        <div className="absolute inset-0 opacity-[0.08] pointer-events-none"
             style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)", backgroundSize: "56px 56px" }} aria-hidden />
        <div className="absolute -top-24 -left-32 h-64 w-96 rotate-[-16deg] opacity-25"
             style={{ background: "linear-gradient(90deg, hsl(var(--bpt-red)) 0%, transparent 100%)" }} aria-hidden />

        <div className="relative z-10">
          <img src="/bpt-logo.webp" alt="Brandpreventie Techniek"
               className="h-12 w-auto object-contain brightness-0 invert opacity-95" />
        </div>

        <div className="relative z-10 space-y-6 max-w-md">
          <div className="flex items-center gap-2">
            <span className="inline-block h-[2px] w-8 bg-[hsl(var(--bpt-red))]" />
            <span className="text-[10px] uppercase tracking-[0.32em] text-white/60 font-semibold">
              BPTplus · Wachtwoordherstel
            </span>
          </div>
          <h1 className="font-heading text-4xl xl:text-5xl font-bold tracking-tighter leading-[1.05]">
            Wachtwoord<br />
            <span className="text-white/50">vergeten?</span>
          </h1>
          <p className="text-white/60 text-sm leading-relaxed max-w-sm">
            Geen probleem. Vul uw e-mailadres in en we sturen u een veilige link waarmee u een nieuw wachtwoord kunt kiezen.
          </p>
        </div>

        <div className="relative z-10 text-[10px] uppercase tracking-widest text-white/50">
          © {new Date().getFullYear()} · Brandpreventie Techniek B.V.
        </div>
      </aside>

      {/* Rechts formulier */}
      <section className="col-span-1 lg:col-span-7 xl:col-span-6 bg-[#fafbfc] flex items-center justify-center p-6 sm:p-10 lg:p-14">
        <div className="w-full max-w-md">
          {sent ? (
            <div data-testid="ww-vergeten-done">
              <div className="inline-flex items-center gap-2 mb-3 text-emerald-600 text-xs font-semibold uppercase tracking-widest">
                <CheckCircle2 className="h-4 w-4" /> Verzonden
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900">
                Controleer uw inbox
              </h2>
              <p className="text-slate-600 mt-3 text-sm leading-relaxed">
                Als het adres <span className="font-mono text-slate-800">{email}</span> bekend is bij ons, ontvangt u binnen enkele minuten een e-mail met een reset-link. Deze link is <strong>60 minuten</strong> geldig.
              </p>
              <div className="mt-6 space-y-3 text-xs text-slate-500">
                <p>• Controleer ook uw spam/ongewenste e-mail.</p>
                <p>• De afzender is <span className="font-mono">contact@brandpreventietechniek.nl</span>.</p>
                <p>• Ontvangt u binnen 15 minuten niks? Neem contact op met it-support.</p>
              </div>
              <div className="mt-8 flex flex-col gap-3">
                <Link to="/login">
                  <Button className="w-full bg-[hsl(var(--bpt-red))] hover:bg-[hsl(var(--bpt-red-dark))]" data-testid="ww-back-login">
                    Terug naar inloggen
                  </Button>
                </Link>
                <button
                  type="button"
                  onClick={() => { setSent(false); setEmail(""); }}
                  className="text-xs text-slate-500 hover:text-slate-800"
                  data-testid="ww-again"
                >
                  Andere aanvraag doen
                </button>
              </div>
            </div>
          ) : (
            <>
              <Link to="/login" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 mb-6" data-testid="ww-terug">
                <ArrowLeft className="h-3 w-3" /> Terug naar inloggen
              </Link>

              <div className="mb-8">
                <div className="flex items-center gap-2 mb-3">
                  <ShieldCheck className="h-4 w-4 text-[hsl(var(--bpt-red))]" />
                  <span className="text-[10px] uppercase tracking-[0.28em] text-slate-500 font-semibold">
                    Wachtwoord herstellen
                  </span>
                </div>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900">
                  Wachtwoord vergeten
                </h2>
                <p className="text-slate-500 text-sm mt-2 leading-relaxed">
                  Vul uw e-mailadres in. U ontvangt binnen enkele minuten een link om uw wachtwoord opnieuw in te stellen.
                </p>
              </div>

              <form onSubmit={onSubmit} data-testid="ww-vergeten-form" className="space-y-5">
                <div>
                  <Label htmlFor="email" className="text-slate-700 text-xs font-semibold uppercase tracking-wider">
                    E-mailadres
                  </Label>
                  <div className="relative mt-2">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      autoComplete="username"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoFocus
                      className="pl-9 h-11 bg-white border-slate-200"
                      placeholder="uw.naam@bedrijf.nl"
                      data-testid="ww-email"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={busy || !email}
                  className="group w-full h-11 bg-[hsl(var(--bpt-red))] hover:bg-[hsl(var(--bpt-red-dark))] text-white font-semibold text-sm tracking-wide"
                  data-testid="ww-submit"
                >
                  {busy ? "Bezig…" : (
                    <span className="inline-flex items-center gap-2">
                      Stuur reset-link
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  )}
                </Button>
              </form>

              <p className="text-xs text-slate-400 mt-8 leading-relaxed">
                Voor uw veiligheid krijgt u altijd dezelfde bevestiging, ook wanneer het adres onbekend is. Zo kan niemand achterhalen welke accounts wel of niet bestaan.
              </p>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
